'use client';
import { useEffect, useState } from 'react';
import { Box } from '@mui/material';
import { useRouter } from 'next/navigation';
import type { SelectChangeEvent } from '@mui/material';
import ControlPanel from '../components/controlPanel';
import IntroDialog from '../components/IntroDialog';
import VtkViewer from '../components/vtkViewer';
import type { BackendPolyDataResponse, FieldOption } from '../types/vtk';

type SimulationSnapshot = {
  data: BackendPolyDataResponse;
  timeROM: number;
  bcROM: number;
  field: FieldOption;
};

type SimulationTimelineState = {
  timeline: SimulationSnapshot[];
  currentIndex: number;
};

const Home = () => {
  const [data, setData] = useState<BackendPolyDataResponse | null>(null);
  const [simulationTimeline, setSimulationTimeline] =
    useState<SimulationTimelineState>({
      timeline: [],
      currentIndex: -1,
    });
  const [loading, setLoading] = useState(false);

  // Control panel state
  const [timeROM, setTimeROM] = useState<number>(0);
  const [bcROM, setBcROM] = useState<number>(1.0);
  const [field, setField] = useState<FieldOption>('agem1');
  const [showIntro, setShowIntro] = useState(true);
  const router = useRouter();

  const applySimulationSnapshot = (snapshot: SimulationSnapshot) => {
    setData(snapshot.data);
    setTimeROM(snapshot.timeROM);
    setBcROM(snapshot.bcROM);
    setField(snapshot.field);
  };

  const fetchData = () => {
    const requestedTimeROM = timeROM;
    const requestedBcROM = bcROM;
    const requestedField = field;

    setLoading(true);

  const token = localStorage.getItem('access_token');

  if (!token) {
    setLoading(false);
    router.replace('/login');
    return;
  }

  const apiUrl = process.env.NEXT_PUBLIC_API_URL;

  if (!apiUrl) {
    setLoading(false);
    console.error('NEXT_PUBLIC_API_URL is not defined');
    return;
  }

  const url =
    `${apiUrl}/run_ROM?time_ROM=${requestedTimeROM}&bc_ROM=${requestedBcROM}&field=${requestedField}`;

    fetch(url, {
      method: 'GET',
      headers: {
        Authorization: `Bearer ${token}`,
      },
    })
      .then(async (response) => {
        if (response.status === 401) {
          localStorage.removeItem('access_token');
          document.cookie = 'access_token=; path=/; max-age=0';
          router.replace('/login');
          return null;
        }

        if (!response.ok) throw new Error('Network response was not ok');
        return response.json();
      })
      .then((responseData: BackendPolyDataResponse) => {
        console.log('Data received:', responseData);

        const nextSimulation = {
          data: responseData,
          timeROM: requestedTimeROM,
          bcROM: requestedBcROM,
          field: requestedField,
        };

        setSimulationTimeline((previousState) => {
          const nextTimeline = previousState.timeline.slice(
            0,
            previousState.currentIndex + 1,
          );
          nextTimeline.push(nextSimulation);

          return {
            timeline: nextTimeline,
            currentIndex: nextTimeline.length - 1,
          };
        });

        applySimulationSnapshot(nextSimulation);
        setLoading(false);
      })
      .catch((err) => {
        console.error('Error fetching data:', err);
        setLoading(false);
      });
  };

  const handleTimeChange = (_: Event, value: number | number[]) => {
    setTimeROM(value as number);
  };

  const handleBcChange = (_: Event, value: number | number[]) => {
    setBcROM(value as number);
  };

  const handleFieldChange = (event: SelectChangeEvent<FieldOption>) => {
    setField(event.target.value as FieldOption);
  };

  const handleUndoSimulation = () => {
    setSimulationTimeline((previousState) => {
      if (previousState.currentIndex <= 0) {
        return previousState;
      }

      const nextIndex = previousState.currentIndex - 1;
      const previousSimulation = previousState.timeline[nextIndex];

      applySimulationSnapshot(previousSimulation);

      return {
        ...previousState,
        currentIndex: nextIndex,
      };
    });
  };

  const handleRedoSimulation = () => {
    setSimulationTimeline((previousState) => {
      if (
        previousState.currentIndex < 0 ||
        previousState.currentIndex >= previousState.timeline.length - 1
      ) {
        return previousState;
      }

      const nextIndex = previousState.currentIndex + 1;
      const nextSimulation = previousState.timeline[nextIndex];

      applySimulationSnapshot(nextSimulation);

      return {
        ...previousState,
        currentIndex: nextIndex,
      };
    });
  };

  const canUndo = !loading && simulationTimeline.currentIndex > 0;
  const canRedo =
    !loading &&
    simulationTimeline.currentIndex >= 0 &&
    simulationTimeline.currentIndex < simulationTimeline.timeline.length - 1;

  useEffect(() => {
    fetchData();
  }, []);

  const handleStartFromIntro = () => {
    setShowIntro(false);
  };

  const handleOpenDescription = () => {
    setShowIntro(true);
  };

  return (
    <>
      <IntroDialog open={showIntro} onStart={handleStartFromIntro} />
      <Box
        component='main'
        sx={{
          width: '100%',
          height: '100dvh',
          maxHeight: '100%',
          minHeight: 0,
          display: 'flex',
          p: 2,
          gap: 2,
          boxSizing: 'border-box',
          overflow: 'hidden',
        }}
      >
        {/* Control Panel - Left Side */}
        <Box
          sx={{
            width: '450px',
            flexShrink: 0,
            height: '100%',
            minHeight: 0,
            overflow: 'hidden',
          }}
        >
          <ControlPanel
            timeROM={timeROM}
            bcROM={bcROM}
            field={field}
            loading={loading}
            canUndo={canUndo}
            canRedo={canRedo}
            onTimeChange={handleTimeChange}
            onBcChange={handleBcChange}
            onFieldChange={handleFieldChange}
            onFetchData={fetchData}
            onOpenDescription={handleOpenDescription}
            onUndo={handleUndoSimulation}
            onRedo={handleRedoSimulation}
          />
        </Box>

        {/* VTK Viewer - Right Side */}
        <Box sx={{ flexGrow: 1, minWidth: 0, height: '100%', minHeight: 0 }}>
          <VtkViewer data={data} loading={loading} />
        </Box>
      </Box>
    </>
  );
};

export default Home;
